import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { fr } from "./fr";

export type Locale = "en" | "fr";

const STORAGE_KEY = "serveboost-locale";
const catalogs: Record<Locale, Record<string, string> | null> = {
  en: null,
  fr
};

type Vars = Record<string, string | number>;

let activeLocale: Locale = "en";

function interpolate(template: string, vars?: Vars) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(vars[key] ?? `{${key}}`));
}

export function translate(locale: Locale, key: string, vars?: Vars) {
  const catalog = catalogs[locale];
  const template = catalog?.[key] ?? key;
  return interpolate(template, vars);
}

/** Translate using the active locale. Safe in render under LocaleProvider. */
export function t(key: string, vars?: Vars) {
  return translate(activeLocale, key, vars);
}

export function getLocale() {
  return activeLocale;
}

export function dateLocaleTag(locale: Locale = activeLocale) {
  return locale === "fr" ? "fr-CA" : "en-US";
}

type LocaleContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: typeof t;
  dateLocale: string;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "fr") return stored;
  } catch {
    /* ignore */
  }
  const browser = typeof navigator !== "undefined" ? navigator.language.toLowerCase() : "en";
  return browser.startsWith("fr") ? "fr" : "en";
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const initial = readStoredLocale();
    activeLocale = initial;
    return initial;
  });

  const setLocale = (next: Locale) => {
    activeLocale = next;
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    activeLocale = locale;
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<LocaleContextValue>(() => ({
    locale,
    setLocale,
    t: (key, vars) => translate(locale, key, vars),
    dateLocale: dateLocaleTag(locale)
  }), [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}

export function useT() {
  return useLocale().t;
}
